import {
    Action,
    Module,
    Mutation,
    VuexModule,
} from 'vuex-module-decorators';

import store from '..';
import axios from 'axios';
import { ILayout, ISignageLayout, IUpdateSignageLayout } from '../interfaces/ISignage';
import { getAuthInstance } from '../../auth';
import { ISqStoreActionResponse } from '../interfaces/ISqStoreActionResponse';

export interface IFetchSignageLayoutsOptions {
    reload?: boolean;
    name?: string;
    sortField?: string;
    sortOrder?: string;
    pageIndex?: number;
    pageSize?: number;
}

export interface ISignageLayoutState {
    readonly allSignageLayouts: ISignageLayout[];
    readonly allLayouts: ILayout[];
    readonly signageLayoutsTotal: number;
    readonly signageLayoutById: (id: string) => ISignageLayout;
    fetchSignageLayouts(reloadOrOptions?: boolean | IFetchSignageLayoutsOptions): Promise<ISqStoreActionResponse<void>>;
    fetchLayouts(): Promise<ISqStoreActionResponse<void>>;
    deleteSignageLayout(signageLayout: ISignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>>;
    createSignageLayout(signageLayout: ISignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>>;
    updateSignageLayout(updatePayload : IUpdateSignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>>;

}

@Module({ dynamic: true, store, name: 'signagelayout' })
export default class SignageLayoutState extends VuexModule implements ISignageLayoutState {
    private signageLayouts: ISignageLayout[] = [];
    private layouts: ILayout[] = [];
    private isSignageLayoutsLoaded = false;
    private signageLayoutsTotalCount = 0;

    //#region GETTERS
    get allSignageLayouts(): ISignageLayout[] {
        const signageLayouts = [...this.signageLayouts.map((x)=>Object.assign({}, x))];
        return signageLayouts;
    }

    // Total matching layouts on the server (for server-side pagination); falls
    // back to the loaded count when the endpoint returned an unpaged array.
    get signageLayoutsTotal(): number {
        return this.signageLayoutsTotalCount || this.signageLayouts.length;
    }

    get allLayouts(): ILayout[] {
        const layouts = [...this.layouts.map((x)=>Object.assign({}, x))];
        return layouts;
    }

    get signageLayoutById(): (signageLayoutId: string) => ISignageLayout {
        return (signageLayoutId: string) => {
            const signageLayout = this.signageLayouts.find(x => x.id === signageLayoutId);
            return signageLayout ? signageLayout : {} as ISignageLayout;
        }
    }
    //#endregion

    //#region ACTIONS
    @Action
    async fetchSignageLayouts(reloadOrOptions?: boolean | IFetchSignageLayoutsOptions): Promise<ISqStoreActionResponse<void>> {
        // Support both the legacy boolean `reload` and the new options object.
        const options: IFetchSignageLayoutsOptions = typeof reloadOrOptions === 'object' && reloadOrOptions !== null
            ? reloadOrOptions
            : { reload: reloadOrOptions };

        if (this.isSignageLayoutsLoaded && !options.reload) return { success: true };

        try {
            const authHeader = await getAuthInstance().getAxiosHeader();
            let fetchSignageLayoutsUrl = `${process.env.VUE_APP_API_HOST}/api/signagelayouts`;
            const queryParams: string[] = [];

            if (options.name) {
                queryParams.push(`name=${encodeURIComponent(options.name)}`);
            }
            if (options.sortField && options.sortOrder) {
                queryParams.push(`sortField=${encodeURIComponent(options.sortField)}`);
                queryParams.push(`sortOrder=${encodeURIComponent(options.sortOrder)}`);
            }
            if (options.pageIndex !== undefined && options.pageSize !== undefined) {
                queryParams.push(`pageIndex=${options.pageIndex}`);
                queryParams.push(`pageSize=${options.pageSize}`);
            }
            if (queryParams.length > 0) {
                fetchSignageLayoutsUrl = `${fetchSignageLayoutsUrl}?${queryParams.join('&')}`;
            }

            const axiosResponse = await axios.get(fetchSignageLayoutsUrl, authHeader);

            // Support both the paged envelope and the legacy bare-array response.
            if (axiosResponse.data && !Array.isArray(axiosResponse.data) && Array.isArray(axiosResponse.data.data)) {
                this.context.commit('setSignageLayouts', axiosResponse.data.data);
                this.context.commit('setSignageLayoutsTotal', axiosResponse.data.total ?? axiosResponse.data.data.length);
            } else {
                this.context.commit('setSignageLayouts', axiosResponse.data);
                this.context.commit('setSignageLayoutsTotal', Array.isArray(axiosResponse.data) ? axiosResponse.data.length : 0);
            }
            this.context.commit('setIsSignageLayoutsLoaded', true);

            return { success: true };
        }
        catch(e:any) {
            console.error('Signage Layout Store Error:', e);
        }
        return { success: false, reason: 'Error fetching signage layouts.' };
    }

    @Action
    async fetchLayouts(): Promise<ISqStoreActionResponse<void>> {
       try {
            const authHeader = await getAuthInstance().getAxiosHeader();
            const fetchLayoutsUrl = `${process.env.VUE_APP_API_HOST}/api/layouts`;
            const axiosResponse = await axios.get(fetchLayoutsUrl, authHeader);

            this.context.commit('setLayouts', axiosResponse.data);

            return { success: true };
        }
        catch(e:any) {
            console.error('Signage Layout Store Error:', e);
        }
        return { success: false, reason: 'Error fetching signage layouts.' };
    }

    @Action
    async deleteSignageLayout(signageLayout: ISignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>> {
        try {
            const authHeader = await getAuthInstance().getAxiosHeader();
            const deleteLayoutUrl = `${process.env.VUE_APP_API_HOST}/api/signagelayouts/${ signageLayout.id }`;
            const axiosResponse = await axios.delete(deleteLayoutUrl, authHeader);
            
            if(signageLayout && signageLayout.id) {
                const mutatedSignageLayouts = [
                    ...this.signageLayouts.filter(x=> !(x.id?.includes(signageLayout.id as any)))
                ];
                this.context.commit('setSignageLayouts', mutatedSignageLayouts);
            }
            return { success: true, data: axiosResponse.data };
        }
        catch(e:any) {
            console.error('Signage Layout Store Error:', e);
        }
        return { success: false, reason: 'Error deleting signage layout.' };
    }

    @Action
    async createSignageLayout(signageLayout: ISignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>>  {
        try {
            const authHeader = await getAuthInstance().getAxiosHeader();
            const createLayoutUrl = `${process.env.VUE_APP_API_HOST}/api/signagelayouts/`;
            const axiosResponse = await axios.post(createLayoutUrl, signageLayout, authHeader);

            if(this.isSignageLayoutsLoaded) {
                this.context.commit('setSignageLayouts', [...this.signageLayouts, axiosResponse.data]);
            }

            return { success: true, data: axiosResponse.data };
        }
        catch(e:any) {
            console.error('Signage Layout Store Error:', e);
        }
        return { success: false, reason: 'Error creating signage layout.' };
    }

    @Action
    async updateSignageLayout(updatePayload : IUpdateSignageLayout): Promise<ISqStoreActionResponse<ISignageLayout>>  {
        try {
            const updateSignageLayoutUrl = `${ process.env.VUE_APP_API_HOST }/api/signagelayouts/${updatePayload.signageLayoutId}`;
            const authHeader = await getAuthInstance().getAxiosHeader();
            const axiosResponse = await axios.put(updateSignageLayoutUrl, updatePayload, authHeader);

            const mutatedSignageLayouts = [
                ...this.signageLayouts.filter(x=>x.id !== updatePayload.signageLayoutId),
                axiosResponse.data
            ];

            this.context.commit('setSignageLayouts', mutatedSignageLayouts);
            return { success: true, data: axiosResponse.data };
        }
        catch(e:any) {
            console.error('Signage Layout Store Error:', e);
        }
        return { success: false, reason: 'Error updating signage layout.' };
    }
    //#endregion

    //#region MUTATIONS
    @Mutation
    async setSignageLayouts(signageLayouts: ISignageLayout[]): Promise<void> {
        this.signageLayouts = signageLayouts;
    }

    @Mutation
    async setSignageLayoutsTotal(total: number): Promise<void> {
        this.signageLayoutsTotalCount = total;
    }

    @Mutation
    async setLayouts(layouts: ILayout[]): Promise<void> {
        this.layouts = layouts;
    }

    @Mutation
    async setIsSignageLayoutsLoaded(isLoaded: boolean): Promise<void> {
        this.isSignageLayoutsLoaded = isLoaded;
    }
    //#endregion

}
